import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { HandHeart, BookOpen, Utensils, HeartPulse, Home as HomeIcon, Shirt, Copy, Check } from "lucide-react";
import SectionTitle from "../components/site/SectionTitle";

const UPI_ID = "8888888987-2@ibl";
const UPI_BANK = "HDFC Bank • A/c ending 0462";
const UPI_QR = `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=2&data=upi://pay?pa=${encodeURIComponent(UPI_ID)}&pn=${encodeURIComponent("Hazrat Sufi Sarkar Trust")}&cu=INR`;

const UpiCard = () => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(UPI_ID);
      setCopied(true);
      toast.success("UPI ID copied to clipboard");
      setTimeout(() => setCopied(false), 2500);
    } catch { toast.error("Could not copy. Long-press to select."); }
  };
  return (
    <div className="mt-12 mx-auto max-w-md card-soft p-6 md:p-8 text-charcoal text-left" data-testid="upi-card">
      <div className="flex items-center gap-3 mb-4">
        <span className="grid place-items-center h-10 w-10 rounded-full bg-emerald text-ivory text-xs font-bold">UPI</span>
        <div>
          <p className="font-serif text-lg text-emerald leading-tight">Scan or send via UPI</p>
          <p className="text-xs text-charcoal-muted">{UPI_BANK}</p>
        </div>
      </div>
      <div className="bg-ivory rounded-md p-4 flex items-center justify-center">
        <img src={UPI_QR} alt="Scan to donate via UPI" width="240" height="240" className="rounded" />
      </div>
      <div className="mt-4 flex items-center justify-between bg-ivory-alt/60 border border-gold/30 rounded-md px-3 py-2">
        <code className="text-sm text-emerald font-semibold" data-testid="upi-id-text">{UPI_ID}</code>
        <button onClick={copy} data-testid="copy-upi-btn" className="inline-flex items-center gap-1 text-xs text-charcoal-muted hover:text-emerald">
          {copied ? <><Check size={14} className="text-gold" /> Copied</> : <><Copy size={14} /> Copy</>}
        </button>
      </div>
      <p className="text-xs text-charcoal-muted mt-3 text-center">Works with PhonePe, Google Pay, Paytm, BHIM and all UPI apps.</p>
    </div>
  );
};

const USES = [
  { icon: BookOpen, title: "Education", desc: "Books, school fees, tutoring for underprivileged children." },
  { icon: HeartPulse, title: "Healthcare", desc: "Medicines, treatments, and hospital support." },
  { icon: Utensils, title: "Food", desc: "Daily meals through langar and ration distribution." },
  { icon: Shirt, title: "Clothing", desc: "Seasonal clothing for families in need." },
  { icon: HomeIcon, title: "Shelter", desc: "Emergency housing support for the destitute." },
  { icon: HandHeart, title: "Seva Initiatives", desc: "Community drives during Ramzan, Eid, and urs days." },
];

export default function Donate() {
  useEffect(() => { document.title = "Donate — Support Our Charitable Mission"; }, []);
  return (
    <div data-testid="donate-page">
      <section className="pattern-emerald text-ivory pt-32 pb-24" data-testid="donate-hero">
        <div className="container-page max-w-3xl text-center">
          <span className="text-[11px] uppercase tracking-[0.32em] text-gold">Support the Mission</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight">Your generosity becomes someone's prayer answered.</h1>
          <p className="text-ivory/80 mt-6 leading-relaxed text-lg">
            Your generosity helps the Hazrat Shahanshah Sufi Sarkar Charitable Trust provide education, healthcare, food, clothing, and shelter to those in need. Every contribution becomes a blessing in someone's life.
          </p>
          <ul className="mt-8 grid sm:grid-cols-2 gap-3 text-left max-w-xl mx-auto text-ivory/85" data-testid="donate-funds-list">
            <li className="flex items-start gap-3"><span className="text-gold mt-1">✦</span><span>Education for underprivileged children</span></li>
            <li className="flex items-start gap-3"><span className="text-gold mt-1">✦</span><span>Healthcare and medicine</span></li>
            <li className="flex items-start gap-3"><span className="text-gold mt-1">✦</span><span>Food, clothing & shelter for the needy</span></li>
            <li className="flex items-start gap-3"><span className="text-gold mt-1">✦</span><span>Community welfare programs</span></li>
          </ul>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a
              data-testid="gpay-donate"
              href="upi://pay?pa=8888888987-2@ibl&pn=Hazrat%20Sufi%20Sarkar%20Trust&cu=INR"
              className="btn-gold !px-10 !py-4 text-base"
            >
              Pay via UPI / GPay
            </a>
          </div>
          <UpiCard />
        </div>
      </section>

      <section className="section-pad" data-testid="donate-uses">
        <div className="container-page">
          <SectionTitle eyebrow="Where it goes" title="Every rupee becomes seva" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {USES.map((u, i) => (
              <div key={u.title} className="card-soft p-8 reveal" style={{ transitionDelay: `${i * 60}ms` }} data-testid={`use-${i}`}>
                <span className="grid place-items-center h-12 w-12 rounded-full border border-gold/50 text-gold mb-5">
                  <u.icon size={20} />
                </span>
                <h3 className="font-serif text-xl text-emerald">{u.title}</h3>
                <p className="text-charcoal-muted mt-3 text-sm leading-relaxed">{u.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pattern-bg section-pad" data-testid="donate-quote">
        <div className="container-page max-w-3xl text-center reveal">
          <p className="calligraphy text-gold text-2xl">"The believer's shade on the Day of Resurrection will be their charity."</p>
          <p className="text-charcoal-muted mt-3 italic">— Hadith</p>
        </div>
      </section>
    </div>
  );
}
